/**
 * useShellOverheat - the Overheats prompt (v8.198.1).
 *
 * Official rule: on a 91+ the wielder either drops the weapon (no damage, Dr
 * condition until the GM hands it back) or holds on and takes the weapon's
 * OWN damage to the arm, soaked by arm armour and Toughness. A local queue
 * like the turn-effect prompts - only the wielder's client fires attacks, so
 * only it ever sees one.
 *
 * Returns `{ pendingOverheat, handleOverheatChoice(choice), handleOverheatDetected(info) }`.
 * Extracted from DeathWatchSimulator; the log lines are unchanged.
 */
import { useState, useCallback } from 'react';
import { mergeCondition } from '../engine/conditions';
import { rollDiceExpression } from '../engine/dice';

/**
 * Holding on: the weapon's own damage to the arm, soaked by arm armour and
 * Toughness Bonus (Unnatural Toughness multiplies it). Rolls the damage.
 */
export function holdOnDamage(req, projection) {
  const raw = rollDiceExpression(req.damageExpression).total;
  const armAP = projection?.armour?.rightArm ?? 0;
  const tbSoak = (projection?.toughnessBonus ?? 3) * (projection?.unnaturalToughness || 1);
  return { raw, soaked: armAP + tbSoak, dealt: Math.max(0, raw - armAP - tbSoak) };
}

/** The hold-on line, word for word. */
export function holdOnText(req, { raw, soaked, dealt }) {
  return (
    '🔥 ' +
    req.slotName +
    ' overheats - the wielder HOLDS ON: ' +
    raw +
    ' ' +
    (req.damageType || 'E') +
    ' to the arm, ' +
    soaked +
    ' soaked → ' +
    dealt +
    ' wound' +
    (dealt === 1 ? '' : 's') +
    '.'
  );
}

/** Drop it: the Dr condition on the wielder, and the arm is spared. */
function dropWeapon(req, deps) {
  if (deps.actor) {
    deps.updateCombatantState(deps.actor.id, (st) => {
      const next = [...(st.conditions ?? [])];
      mergeCondition(next, 'Dr', deps.stackPolicy);
      return { ...st, conditions: next };
    });
  }
  deps.addNarrative(deps.activeTab, {
    type: 'info',
    text: '🔥 ' + req.slotName + ' overheats - the wielder DROPS it and spares the arm.',
    timestamp: Date.now(),
  });
}

/** Hold on: the wounds off the wielder, when there is one and anything got through. */
function holdOn(req, deps) {
  const hit = holdOnDamage(req, deps.actorProjection);
  if (deps.actor && hit.dealt > 0) {
    deps.updateCombatantState(deps.actor.id, (st) => ({ ...st, woundsCurrent: (st.woundsCurrent ?? 0) - hit.dealt }));
  }
  deps.addNarrative(deps.activeTab, { type: 'info', text: holdOnText(req, hit), timestamp: Date.now() });
}

/** One answer to the front of the queue, behind its own undo step. */
function answerOverheat(req, choice, deps) {
  if (!req) return;
  deps.capturePreActionSnapshot();
  deps.pushHistory();
  if (choice === 'drop') dropWeapon(req, deps);
  else holdOn(req, deps);
}

/**
 * @param {object} args - actor, actorProjection, activeTab, stackPolicy,
 *   and the writers: updateCombatantState, addNarrative,
 *   capturePreActionSnapshot, pushHistory.
 *
 * The choice callback depends on everything it reads; it once omitted
 * `stackPolicy` and served the previous policy after a change (v8.377.3).
 */
export default function useShellOverheat(args) {
  const { actor, actorProjection, activeTab, stackPolicy } = args;
  const { updateCombatantState, addNarrative, capturePreActionSnapshot, pushHistory } = args;
  const [overheatQueue, setOverheatQueue] = useState([]);
  const handleOverheatChoice = useCallback(
    (choice) => {
      setOverheatQueue((q) => q.slice(1));
      const deps = { actor, actorProjection, activeTab, stackPolicy, updateCombatantState, addNarrative };
      answerOverheat(overheatQueue[0], choice, { ...deps, capturePreActionSnapshot, pushHistory });
    },
    [
      stackPolicy,
      overheatQueue,
      actor,
      actorProjection,
      activeTab,
      addNarrative,
      updateCombatantState,
      capturePreActionSnapshot,
      pushHistory,
    ],
  );
  const handleOverheatDetected = useCallback((info) => setOverheatQueue((q) => [...q, info]), []);
  return { pendingOverheat: overheatQueue[0] ?? null, handleOverheatChoice, handleOverheatDetected };
}
